import React from 'react';
import { X, Calendar, Activity, TrendingUp } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { useStaffDetailQuery } from '../../api/reportsApi';
import ChartBox from '../../components/ChartBox';
import ExportButtons from '../../components/ExportButtons';
import { doctorDisplayName } from '../appointments/doctorColors';
import { procedureLabel } from '../appointments/appointmentStatus';

const fmtINR = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v ?? 0);
const fmtDateTime = (d) => new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
const pct = (r) => `${Math.round((r ?? 0) * 100)}%`;

function Stat({ label, value, accent }) {
  return (
    <div className="card" style={{ padding: '12px 14px' }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: '1.2rem', fontWeight: 800, marginTop: 4, color: accent || 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}

export default function StaffPerformanceDrawer({ userId, onClose }) {
  const { data: d, isLoading } = useStaffDetailQuery({ userId }, { skip: !userId });
  if (!userId) return null;

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isLoading ? 'Loading…' : doctorDisplayName(d?.fullName ?? 'Staff')}</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {d && (
              <ExportButtons
                filename={`doctor-profile-${(d.fullName || 'staff').replace(/\s+/g, '-').toLowerCase()}`}
                title={`Doctor Profile — ${doctorDisplayName(d.fullName)}`}
                subtitle="Recent appointments"
                columns={[
                  { key: 'staff', label: 'Staff' }, { key: 'patient', label: 'Patient' },
                  { key: 'doctor', label: 'Doctor' }, { key: 'when', label: 'Date/time' },
                  { key: 'procedure', label: 'Procedure' }, { key: 'status', label: 'Status' },
                ]}
                getRows={() => (d.recentAppointments ?? []).map((a) => ({
                  staff: doctorDisplayName(d.fullName), patient: a.patientName ?? '—',
                  doctor: doctorDisplayName(d.fullName), when: fmtDateTime(a.scheduledAt),
                  procedure: procedureLabel(a) ?? '—', status: a.status,
                }))}
              />
            )}
            <button className="modal-close" onClick={onClose}><X size={18} /></button>
          </div>
        </div>
        <div className="modal-body">
          {d && (
            <>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {d.roles?.map((r) => <span key={r} className="badge b-blue">{r}</span>)}
                {d.specialization && <span className="badge b-gray">{d.specialization}</span>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                <Stat label="Consultations" value={d.consultationsTaken} />
                <Stat label="Sessions conducted" value={d.sessionsConducted} accent="var(--green-700)" />
                <Stat label="Patients handled" value={d.patientsHandled} />
                <Stat label="Continued treatment" value={d.patientsContinuedAfterConsultation} />
                <Stat label="Conversion rate" value={pct(d.consultationToTreatmentRate)} accent="var(--blue-800)" />
                <Stat label="Revenue (attributed)" value={fmtINR(d.revenueAttributed)} accent="var(--green-700)" />
                <Stat label="Rating" value={d.averageRating != null ? d.averageRating.toFixed(1) : '—'} />
              </div>

              <h3 style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)', margin: '18px 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <TrendingUp size={15} /> Monthly activity
              </h3>
              <ChartBox height={220}>
                <BarChart data={d.monthly ?? []}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" fontSize={11} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="consultations" name="Consultations" fill="var(--blue-500)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="sessions" name="Sessions" fill="var(--green-500)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartBox>

              {d.procedureBreakdown?.length > 0 && (
                <>
                  <h3 style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)', margin: '18px 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Activity size={15} /> Procedure breakdown
                  </h3>
                  {d.procedureBreakdown.map((p) => (
                    <div key={p.name} className="info-row">
                      <div className="ir-label" style={{ minWidth: 'auto', flex: 1 }}>{p.name}</div>
                      <div className="ir-value">{p.count}</div>
                    </div>
                  ))}
                </>
              )}

              <h3 style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)', margin: '18px 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Calendar size={15} /> Recent appointments
              </h3>
              {d.recentAppointments?.length ? (
                <div className="tbl-wrap">
                  <table>
                    <thead><tr><th>When</th><th>Patient</th><th>Procedure</th><th>Status</th></tr></thead>
                    <tbody>
                      {d.recentAppointments.map((a) => (
                        <tr key={a.id}>
                          <td className="td-muted">{fmtDateTime(a.scheduledAt)}</td>
                          <td>{a.patientName ?? '—'}</td>
                          <td className="td-muted">{procedureLabel(a) ?? '—'}</td>
                          <td><span className="badge b-gray">{a.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <div className="td-muted" style={{ fontSize: '0.83rem' }}>No appointments in range.</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
